import { FC, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import graphql from 'babel-plugin-relay/macro';

import FlightIcon from '@mui/icons-material/Flight';
import TableRow from '@mui/material/TableRow';
import Table from 'components/Table';
import TableCell from 'components/TableCell';
import TableHeadCell from 'components/TableHeadCell';
import FlightListRow from 'components/FlightListRow';
import FlightListToolbar from 'components/FlightListToolbar';

import { useAvoidSuspense, useSkipFirstLoad, useDelete } from 'lib';
import { useSearch } from 'components/Search';
import { useTableSort } from 'components/TableSorting';
import { usePagination } from 'components/Pagination';

import Query, {
  FlightFilters,
  FlightListRefetchQuery,
} from '__generated__/FlightListRefetchQuery.graphql';
import { FlightList_data$key } from '__generated__/FlightList_data.graphql';

type Props = {
  data: FlightList_data$key;
};

export function makeFilter(searchParams: URLSearchParams): FlightFilters {
  const f: FlightFilters = {};

  if (searchParams.get('expired')) {
    f.expired = searchParams.get('expired') === '1' ? true : false;
  }

  if (searchParams.get('isUpdating')) {
    f.isUpdating = searchParams.get('isUpdating') === '1' ? true : false;
  }

  return f;
}

const FlightList: FC<Props> = props => {
  const [searchParams] = useSearchParams();
  const { search } = useSearch();
  const { sort } = useTableSort();
  const { variables } = usePagination();

  const [data, { refetch, loading }] = useAvoidSuspense<
    FlightListRefetchQuery,
    FlightList_data$key
  >(
    graphql`
      fragment FlightList_data on Query
      @refetchable(queryName: "FlightListRefetchQuery") {
        flights(
          first: $first
          after: $after
          filters: $filters
          search: $search
          sort: $sort
        ) {
          __id
          edges {
            node {
              id

              ...FlightListRow_flight
            }
          }

          pageInfo {
            ...Table_pageInfo
          }
        }
      }
    `,
    props.data,
    Query
  );

  const { __id, edges, pageInfo } = data.flights ?? {};

  const [deleteFlight, deleting] = useDelete(__id);

  useSkipFirstLoad(
    useCallback(() => {
      refetch({
        ...variables,
        search,
        sort,
        filters: makeFilter(searchParams),
      });
    }, [refetch, search, searchParams, sort, variables])
  );

  return (
    <Table
      search
      pagination
      icon={<FlightIcon />}
      pageInfo={pageInfo ?? null}
      loading={loading || deleting}
      toolbar={<FlightListToolbar />}
      head={
        <TableRow>
          <TableCell>Skrydis</TableCell>

          <TableHeadCell center name="priceLive">
            Kaina
          </TableHeadCell>

          <TableHeadCell center name="lastPriceChange">
            Kaina keitėsi
          </TableHeadCell>

          <TableHeadCell center name="createdAt">
            Įvesta
          </TableHeadCell>

          <TableHeadCell center name="lastSuccessfulUpdate">
            Atnaujinta
          </TableHeadCell>

          <TableHeadCell center name="provider">
            Tiekėjas
          </TableHeadCell>

          <TableHeadCell center name="expired">
            Būsena
          </TableHeadCell>

          <TableCell />
        </TableRow>
      }>
      {edges?.map(({ node }) => (
        <FlightListRow key={node.id} flight={node} onDelete={deleteFlight} />
      ))}
    </Table>
  );
};

export default FlightList;
